/** RFC 0357: the skill catalogs are projections of ordinary SKILL.md docs. */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { projectDocuments } from '../documents/reads'
import type { DocumentDoc } from '../documents/types'
import { changeProject, projectString } from '../project/scalars'
import type { ProjectDoc } from '../project/types'
import type { WorldSkill } from '../world/types'

const PACKAGE = /^skills\/([^/]+)\/SKILL\.md$/

type Catalog = { skills?: Record<string, WorldSkill> }

async function findReady<T>(
  repo: Repo,
  id: string,
): Promise<DocHandle<T> | null> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      (async () => {
        const handle = await repo.find<T>(id as AnyDocumentId)
        await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
        return handle
      })(),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), 2000)
        ;(timer as { unref?: () => void }).unref?.()
      }),
    ])
  } catch {
    return null
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** Rebuild one World or project catalog when its package paths or timestamps
 * differ. A matching catalog costs no per-skill document hydration. */
export async function reconcileSkillCatalog(
  repo: Repo,
  catalogId: string,
  packageProjectId: string,
): Promise<boolean> {
  const [catalog, project] = await Promise.all([
    findReady<Catalog>(repo, catalogId),
    findReady<ProjectDoc>(repo, packageProjectId),
  ])
  if (!catalog || !project) return false
  const refs = projectDocuments(project.doc()).flatMap((ref) => {
    const match = PACKAGE.exec(ref.path)
    return match ? [{ ref, match }] : []
  })
  const byId = new Map(refs.map(({ ref, match }) => [match[1], ref]))
  const indexed = catalog.doc()?.skills ?? {}
  const stale = Object.entries(indexed).filter(([id, entry]) => {
    const ref = byId.get(id)
    return (
      !ref ||
      projectString(entry.link) !== `automerge:${ref.id}` ||
      entry.sourceUpdatedAt !== ref.updatedAt
    )
  })
  const missing = refs.filter(({ match }) => !indexed[match[1]])
  if (!stale.length && !missing.length) return false
  if (
    !missing.length &&
    stale.every(
      ([id, entry]) => !byId.has(id) && entry.sourceUpdatedAt === undefined,
    )
  )
    return false

  const desired = new Map<string, WorldSkill>()
  for (const { ref, match } of refs) {
    const doc = await findReady<DocumentDoc>(repo, ref.id)
    if (!doc) {
      if (indexed[match[1]]) desired.set(match[1], indexed[match[1]])
      continue
    }
    const data = doc.doc()
    if (data?.workspaceId !== packageProjectId || data.path !== ref.path)
      continue
    const previous = indexed[match[1]]
    const title = data.skill?.title || previous?.title || ref.title
    const description = data.skill?.description ?? previous?.description ?? ''
    if (!data.skill) {
      // A manually added SKILL.md also acquires durable metadata, so a later
      // loss of the index cannot erase its description.
      doc.change((d) => {
        d.skill = { title, description }
      })
    }
    desired.set(match[1], {
      title,
      description,
      link: `automerge:${ref.id}`,
      sourceUpdatedAt: ref.updatedAt,
    })
  }

  // Pre-0357 links are kept until the migration moves them into packages.
  for (const [id, entry] of stale) {
    if (byId.has(id)) continue
    if (entry.sourceUpdatedAt === undefined) desired.set(id, entry)
  }
  changeProject(catalog, (d) => {
    if (!d.skills) d.skills = {}
    for (const id of Object.keys(d.skills)) {
      if (!desired.has(id)) delete d.skills[id]
    }
    for (const [id, entry] of desired) {
      const current = d.skills[id]
      if (!current) {
        d.skills[id] = entry
        continue
      }
      // projectString: a scalar never `!==` its string, and rewriting it here
      // would turn the S11 form back into per-character text every pass.
      if (projectString(current.title) !== entry.title)
        current.title = entry.title
      if (projectString(current.description) !== entry.description)
        current.description = entry.description
      if (projectString(current.link) !== entry.link) current.link = entry.link
      if (current.sourceUpdatedAt !== entry.sourceUpdatedAt)
        current.sourceUpdatedAt = entry.sourceUpdatedAt
    }
  })
  return true
}
